import { getActionErrorMessage } from '@hit-app/convex/actionErrors';
import { getInitials } from '@hit-app/convex/format';
import { getRoleLabel } from '@hit-app/convex/roles';
import { useEffect, useRef, useState } from 'react';
import { useWorkOSAuth } from '../lib/auth';
import { reportPostHogError } from '../lib/posthog';
import type { UserOrganizations } from '../lib/useUserOrganizations';

interface OrganizationSwitcherProps {
  /** Result of `useUserOrganizations()`, loaded by the parent so it can react to an empty list. */
  organizations: UserOrganizations;
  variant?: 'default' | 'sidebar';
}

/**
 * Custom organization switcher that matches the dark luxury theme.
 * Replaces the WorkOS widget which renders unstyled native elements.
 */
export function OrganizationSwitcher({ organizations, variant = 'default' }: OrganizationSwitcherProps) {
  const { organizationId, switchToOrganization } = useWorkOSAuth();
  const { memberships, isLoading: loading } = organizations;
  const isSidebar = variant === 'sidebar';
  const [switchError, setSwitchError] = useState<string | null>(null);
  const error = switchError ?? organizations.error;
  const [isOpen, setIsOpen] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  const currentMembership = memberships.find((membership) => membership.organizationId === organizationId) ?? null;
  const otherMemberships = memberships.filter((membership) => membership.organizationId !== organizationId);

  const handleSwitch = async (orgId: string) => {
    setSwitching(orgId);
    setSwitchError(null);
    try {
      await switchToOrganization({ organizationId: orgId });
      // AuthKitProvider then updates organizationId, which remounts ConvexProviderWithAuthKit (keyed on orgId).
    } catch (err) {
      console.error('Error switching organization:', err);
      reportPostHogError(err, {
        area: 'organization',
        action: 'switch_organization',
        route: '/organization-switcher',
        organizationId,
        properties: {
          target_organization_id: orgId,
        },
      });
      setSwitchError(getActionErrorMessage(err, 'Unbekannter Fehler beim Wechseln des Vereins'));
      setSwitching(null);
      setIsOpen(false);
    }
  };

  if (loading) {
    return (
      <div className={isSidebar ? 'org-switcher-loading-sidebar' : 'org-switcher-loading'}>
        <div className="org-switcher-spinner" />
      </div>
    );
  }

  if (error && memberships.length === 0) {
    return (
      <div className="org-switcher-error">
        <p className="org-switcher-error-title">Fehler</p>
        <p className="org-switcher-error-msg">{error}</p>
      </div>
    );
  }

  if (memberships.length === 0) {
    return null;
  }

  return (
    <div className="org-switcher" ref={dropdownRef}>
      <p className="org-switcher-label">Ihre Vereine</p>

      {/* Trigger button */}
      <button
        type="button"
        className={`org-switcher-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {currentMembership?.logoUrl ? (
          <img
            src={currentMembership.logoUrl}
            alt=""
            className="org-switcher-avatar"
            style={{ objectFit: 'contain', padding: '3px' }}
          />
        ) : (
          <span className="org-switcher-avatar">
            {currentMembership ? getInitials(currentMembership.organizationName) : '?'}
          </span>
        )}
        <span className="org-switcher-trigger-info">
          <span className="org-switcher-trigger-name">{currentMembership?.organizationName ?? 'Verein wählen'}</span>
          <span className="org-switcher-trigger-role">
            {currentMembership ? getRoleLabel(currentMembership.role) : 'Mitglied'}
          </span>
        </span>
        <svg
          className={`org-switcher-chevron ${isOpen ? 'rotated' : ''}`}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && otherMemberships.length > 0 && (
        <div className="org-switcher-dropdown" role="listbox">
          <p className="org-switcher-dropdown-label">Wechseln zu</p>
          {otherMemberships.map((membership) => (
            <button
              key={membership.id}
              type="button"
              role="option"
              aria-selected={false}
              className="org-switcher-option"
              disabled={switching === membership.organizationId}
              onClick={() => void handleSwitch(membership.organizationId)}
            >
              {membership.logoUrl ? (
                <img
                  src={membership.logoUrl}
                  alt=""
                  className="org-switcher-option-avatar"
                  style={{ objectFit: 'contain', padding: '2px' }}
                />
              ) : (
                <span className="org-switcher-option-avatar">{getInitials(membership.organizationName)}</span>
              )}
              <span className="org-switcher-option-info">
                <span className="org-switcher-option-name">{membership.organizationName}</span>
                <span className="org-switcher-option-role">{getRoleLabel(membership.role)}</span>
              </span>
              {switching === membership.organizationId && <div className="org-switcher-option-spinner" />}
            </button>
          ))}
        </div>
      )}

      {error && memberships.length > 0 && <p className="org-switcher-inline-error">{error}</p>}
    </div>
  );
}
